/**
 * The admin area (Phase 26) — what a platform admin sees after signing in.
 *
 * Every action is checked by the database (0033/0036); this page only avoids
 * offering what would be refused. Seeing inside a firm needs a support
 * session: opening one reloads into that firm's app, read-only (F8).
 */

import { signOut } from '@/lib/auth';
import { esc, fmtDate, setPageTitle } from '@/lib/dom';
import { toastError, toastSuccess } from '@/lib/toast';
import {
  listFirms, createFirm, inviteFirmOwner, setFirmStatus, addPlatformAdmin,
  startSupportSession, listSupportSessions, type AdminFirm, type SupportLogEntry,
} from '@/lib/data/admin';
import {
  slugFromName, createFirmProblem, supportReasonProblem, seatsText, firmRowActions, sessionState,
} from '@/lib/admin';

export interface AdminOptions {
  email: string;
  /** Called after a support session opens, to re-enter the app in support mode. */
  onSupportStarted: () => void;
}

export async function renderAdmin(root: HTMLElement, opts: AdminOptions): Promise<void> {
  setPageTitle('Admin', 'Design Studio HQ');
  root.innerHTML = `<div class="admin"><div class="loading">Loading…</div></div>`;
  let firms: AdminFirm[] = [];
  let log: SupportLogEntry[] = [];
  try {
    [firms, log] = await Promise.all([listFirms(), listSupportSessions(20)]);
  } catch (err) {
    root.innerHTML = `<div class="admin"><div class="auth-error">${
      esc(err instanceof Error ? err.message : 'Could not load the admin area.')}</div></div>`;
    return;
  }
  paint(root, opts, firms, log);
}

function paint(root: HTMLElement, opts: AdminOptions, firms: AdminFirm[], log: SupportLogEntry[]): void {
  const reload = (): void => { void renderAdmin(root, opts); };
  const byId = new Map(firms.map((f) => [f.id, f] as const));

  root.innerHTML = `
    <div class="admin">
      <header class="admin-head">
        <div>
          <div class="admin-title">Design Studio HQ</div>
          <div class="muted">Admin · ${esc(opts.email)}</div>
        </div>
        <button class="btn-link" data-action="sign-out">Sign out</button>
      </header>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Firms</h3></div>
          <span class="muted">${firms.length}</span></div>
        ${firms.length === 0 ? `<div class="empty-inline">No firms yet.</div>` : `
        <ul class="admin-firms">
          ${firms.map((f) => {
            const a = firmRowActions(f);
            return `
            <li class="admin-firm" data-firm="${esc(f.id)}">
              <div class="admin-firm-main">
                <div class="admin-firm-name">${esc(f.name)}
                  ${f.status === 'suspended' ? '<span class="chip chip-suspended">Paused</span>' : ''}</div>
                <div class="muted small">${f.slug ? esc(f.slug) + ' · ' : ''}${esc(seatsText(f))}</div>
                <div class="muted small">${a.needsOwner ? 'No owner yet' : `Owner: ${esc(f.owners ?? '')}`}</div>
              </div>
              <div class="admin-firm-actions">
                ${a.needsOwner ? `<button class="btn-link" data-action="invite-owner" data-firm="${esc(f.id)}">Invite owner</button>` : ''}
                ${a.canSupport ? `<button class="btn-link" data-action="support" data-firm="${esc(f.id)}">Open support session</button>` : ''}
                ${a.canSuspend ? `<button class="btn-link btn-danger-link" data-action="suspend" data-firm="${esc(f.id)}">Pause</button>` : ''}
                ${a.canReactivate ? `<button class="btn-link" data-action="reactivate" data-firm="${esc(f.id)}">Reactivate</button>` : ''}
              </div>
            </li>`;
          }).join('')}
        </ul>`}
      </section>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>New firm</h3></div></div>
        <form class="admin-form" data-form="create-firm" novalidate>
          <label class="field"><span>Firm name</span>
            <input name="name" type="text" autocomplete="off" placeholder="Anderson Interiors" /></label>
          <label class="field"><span>Short name (optional — their future web address)</span>
            <input name="slug" type="text" autocomplete="off" placeholder="andersoninteriors" /></label>
          <label class="field"><span>Owner’s email</span>
            <input name="owner" type="email" autocomplete="off" placeholder="owner@example.com" /></label>
          <div class="auth-error" data-error hidden></div>
          <button type="submit" class="btn-primary">Create firm and invite owner</button>
        </form>
      </section>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Add an admin</h3></div></div>
        <form class="admin-form" data-form="add-admin" novalidate>
          <label class="field"><span>Email of an existing account that isn’t in a firm</span>
            <input name="email" type="email" autocomplete="off" /></label>
          <div class="auth-error" data-error hidden></div>
          <button type="submit" class="btn-primary">Make admin</button>
        </form>
      </section>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Support sessions</h3></div>
          <span class="muted">Every session is kept</span></div>
        ${log.length === 0 ? `<div class="empty-inline">None yet.</div>` : `
        <ul class="admin-log">
          ${log.map((s) => `
            <li>
              <div><strong>${esc(s.firm_name)}</strong> · ${esc(s.admin_email)} · ${esc(fmtDate(s.started_at))}
                <span class="muted">· ${esc(sessionState(s))}</span></div>
              <div class="muted small">${esc(s.reason)}</div>
            </li>`).join('')}
        </ul>`}
      </section>
    </div>`;

  root.querySelectorAll('[data-action="sign-out"]').forEach((el) =>
    el.addEventListener('click', () => { void signOut(); }));

  root.querySelectorAll<HTMLButtonElement>('[data-action="suspend"], [data-action="reactivate"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const f = byId.get(btn.dataset.firm ?? '');
      if (!f) return;
      const suspend = btn.dataset.action === 'suspend';
      if (suspend && !confirm(`Pause ${f.name}?\n\nIts people can still sign in, but will see “Account paused” instead of their projects. Nothing is deleted.`)) return;
      btn.disabled = true;
      try {
        await setFirmStatus(f.id, suspend ? 'suspended' : 'active');
        toastSuccess(suspend ? `${f.name} paused` : `${f.name} reactivated`);
        reload();
      } catch (err) {
        btn.disabled = false;
        toastError(err instanceof Error ? err.message : 'That didn’t work.');
      }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action="invite-owner"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const f = byId.get(btn.dataset.firm ?? '');
      const email = f ? prompt(`Email of ${f.name}’s owner:`) : null;
      if (!f || !email) return;
      btn.disabled = true;
      try {
        await inviteFirmOwner(f.id, email);
        toastSuccess(`Invitation sent to ${email.trim()}`);
        reload();
      } catch (err) {
        btn.disabled = false;
        toastError(err instanceof Error ? err.message : 'The invitation couldn’t be sent.');
      }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action="support"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const f = byId.get(btn.dataset.firm ?? '');
      if (!f) return;
      const reason = prompt(`Why are you opening ${f.name}? This is recorded, and their owner can see it.`);
      if (reason === null) return;
      const problem = supportReasonProblem(reason);
      if (problem) { toastError(problem); return; }
      btn.disabled = true;
      try {
        await startSupportSession(f.id, reason, 60);
        opts.onSupportStarted();
      } catch (err) {
        btn.disabled = false;
        toastError(err instanceof Error ? err.message : 'Could not open a support session.');
      }
    });
  });

  root.querySelectorAll<HTMLFormElement>('[data-form="create-firm"]').forEach((form) => {
    const nameInput = form.querySelector<HTMLInputElement>('input[name="name"]')!;
    const slugInput = form.querySelector<HTMLInputElement>('input[name="slug"]')!;
    // Suggest a short name from the firm name until one is typed by hand.
    let slugTouched = false;
    slugInput.addEventListener('input', () => { slugTouched = true; });
    nameInput.addEventListener('input', () => { if (!slugTouched) slugInput.value = slugFromName(nameInput.value); });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errBox = form.querySelector<HTMLElement>('[data-error]')!;
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      const fd = new FormData(form);
      const name = String(fd.get('name') ?? '');
      const slug = String(fd.get('slug') ?? '');
      const owner = String(fd.get('owner') ?? '');
      errBox.hidden = true;
      const problem = createFirmProblem(name, slug, owner);
      if (problem) { errBox.textContent = problem; errBox.hidden = false; return; }

      button.disabled = true;
      let firmId: string;
      try {
        firmId = await createFirm(name, slug.trim() || null);
      } catch (err) {
        errBox.textContent = err instanceof Error ? err.message : 'Could not create the firm.';
        errBox.hidden = false;
        button.disabled = false;
        return;
      }
      try {
        await inviteFirmOwner(firmId, owner);
        toastSuccess(`${name.trim()} created — invitation sent to ${owner.trim()}`);
      } catch (err) {
        // The firm exists; its row will offer "Invite owner" to retry.
        toastError(`${name.trim()} was created, but the invitation failed: ${
          err instanceof Error ? err.message : 'unknown error'}. Use “Invite owner” to try again.`);
      }
      reload();
    });
  });

  root.querySelectorAll<HTMLFormElement>('[data-form="add-admin"]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errBox = form.querySelector<HTMLElement>('[data-error]')!;
      const email = String(new FormData(form).get('email') ?? '').trim();
      errBox.hidden = true;
      if (!email) { errBox.textContent = 'Enter an email address.'; errBox.hidden = false; return; }
      try {
        await addPlatformAdmin(email);
        toastSuccess(`${email} is now an admin`);
        form.reset();
      } catch (err) {
        errBox.textContent = err instanceof Error ? err.message : 'That didn’t work.';
        errBox.hidden = false;
      }
    });
  });
}
